import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PaywallButton } from "@/components/ui/PaywallButton";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Circle,
  Loader2,
  Trash2,
} from "lucide-react";
import { Sector, SectorAnalysis } from "@/types/risk-analysis";
import { getCyFunLevelData } from "@/utils/risk-calculator";
import { formatScore } from "@/lib/utils";
import { CyFunLevelBadge } from "./CyFunLevelBadge";

interface SectorCardProps {
  sector: Sector;
  analysis?: SectorAnalysis;
  onNavigate: () => void;
  onDelete?: () => void;
  className?: string;
  disabled?: boolean;
  isNavigatePending?: boolean;
  isNavigateLocked?: boolean;
}

export function SectorCard({
  sector,
  analysis,
  onNavigate,
  onDelete,
  className,
  disabled = false,
  isNavigatePending = false,
  isNavigateLocked = false,
}: SectorCardProps) {
  const getProgressIcon = () => {
    if (!analysis || !analysis.isComplete) {
      return <Circle className="h-4 w-4 text-muted-foreground" />;
    }
    if (analysis.isComplete) {
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    }
    return <Clock className="h-4 w-4 text-primary" />;
  };

  const getProgressLabel = () => {
    if (!analysis) return "Neînceput";
    if (analysis.isComplete) return "Completat";
    return "În progres";
  };

  const getProgressColor = () => {
    if (!analysis) return "text-muted-foreground";
    if (analysis.isComplete) return "text-green-600";
    return "text-primary";
  };

  return (
    <Card
      className={`hover:shadow-md transition-shadow flex flex-col ${className || ""}`}
    >
      <CardHeader className="pb-3 md:pb-0 md:px-3 md:pt-4">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base md:text-lg">
              {sector.nume}
            </CardTitle>
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                className="h-8 w-8 p-0 shrink-0 hover:bg-destructive hover:text-destructive-foreground"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <p className="text-xs md:text-sm text-muted-foreground line-clamp-2 min-h-8 md:min-h-10">
            {sector.subsectoare}
          </p>
          <div>
            <Badge
              variant={sector.annex === "I" ? "default" : "secondary"}
              className="text-xs"
            >
              Anexa {sector.annex}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col pt-1 md:pt-3 md:px-3 md:pb-4 gap-2 justify-end">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            {getProgressIcon()}
            <span className={`text-sm font-medium ${getProgressColor()}`}>
              {getProgressLabel()}
            </span>
          </div>

          {analysis && (
            <div className="grid gap-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Scor Risc:</span>
                <div
                  className={`rounded-lg font-bold text-center text-lg px-2 py-1 ${getCyFunLevelData(analysis.cyfunLevel).color}`}
                >
                  {formatScore(analysis.totalScore)}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Nivel CyFun:</span>
                <CyFunLevelBadge level={analysis.cyfunLevel} />
              </div>
            </div>
          )}
        </div>

        <PaywallButton
          locked={!disabled && !isNavigatePending && isNavigateLocked}
          rightIcon={
            isNavigatePending ? (
              <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="ml-2 h-4 w-4" />
            )
          }
          onClick={disabled || isNavigatePending ? undefined : onNavigate}
          className="w-full mt-3"
          variant={analysis?.isComplete ? "outline" : "default"}
          disabled={disabled || isNavigatePending}
        >
          {disabled
            ? "Setați dimensiunea"
            : analysis?.isComplete
              ? "Vezi Detalii"
              : "Începe Analiza"}
        </PaywallButton>
      </CardContent>
    </Card>
  );
}
